import React, { useState, useEffect } from 'react';
import { SponsorService, InvitationRequest } from '../services/sponsorService';
import styles from '../styles/inviteManagement.module.css';

interface SentInvitation {
  invitation_id: string;
  to_email: string;
  relationship_type: 'sponsor' | 'sponsee' | 'friend';
  message: string;
  program?: string;
  status: 'pending' | 'accept' | 'decline';
  created_at: string;
  accepted_at?: string;
}

interface InviteManagementProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedRelationship?: 'sponsor' | 'sponsee' | 'friend';
  preselectedProgram?: string;
}

const InviteManagement: React.FC<InviteManagementProps> = ({
  isOpen,
  onClose,
  preselectedRelationship,
  preselectedProgram
}) => {
  // Send form state
  const [email, setEmail] = useState('');
  const [relationshipType, setRelationshipType] = useState<'sponsor' | 'sponsee' | 'friend'>(
    preselectedRelationship || 'friend'
  );
  const [program, setProgram] = useState(preselectedProgram || 'SAA');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [justSent, setJustSent] = useState(false);

  // Sent invitations history state
  const [sentInvitations, setSentInvitations] = useState<SentInvitation[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Load sent invitations when modal opens
  useEffect(() => {
    if (isOpen) {
      loadSentInvitations();
      // Reset form
      setEmail('');
      setRelationshipType(preselectedRelationship || 'friend');
      setProgram(preselectedProgram || 'SAA');
      setMessage('');
      setSendError(null);
      setJustSent(false);
    }
  }, [isOpen, preselectedRelationship, preselectedProgram]);

  const loadSentInvitations = async () => {
    try {
      setLoadingHistory(true);
      setHistoryError(null);
      const data = await SponsorService.getSentInvitations();
      setSentInvitations(data);
    } catch (err: any) {
      setHistoryError('Failed to load invitation history');
      console.error('Error loading sent invitations:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !relationshipType) {
      setSendError('Please fill in all required fields');
      return;
    }

    try {
      setSending(true);
      setSendError(null);

      const request: InvitationRequest = {
        to_email: email,
        relationship_type: relationshipType,
        message,
        program: (relationshipType === 'sponsor' || relationshipType === 'sponsee') ? program : undefined
      };

      await SponsorService.sendInvitation(request);
      
      // Reset form
      setEmail('');
      setMessage('');
      setJustSent(true);
      
      // Reload history
      await loadSentInvitations();
      
      // Clear success message after 3 seconds
      setTimeout(() => setJustSent(false), 3000);
    } catch (err: any) {
      setSendError(err.message || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  const relationshipLabels = {
    sponsor: 'Sponsor',
    sponsee: 'Sponsee',
    friend: 'Recovery Friend'
  };

  const statusLabels = {
    pending: 'Pending',
    accept: 'Accepted',
    decline: 'Declined'
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Invite Someone to Recovery Commons</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* SEND INVITATION FORM */}
          <div className={styles.sendSection}>
            {justSent && (
              <div className={styles.successBanner}>
                ✓ Invitation sent successfully!
              </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="email" className={styles.label}>
                    Email Address <span className={styles.required}>*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="their.email@example.com"
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="relationship" className={styles.label}>
                    Relationship <span className={styles.required}>*</span>
                  </label>
                  <select
                    id="relationship"
                    value={relationshipType}
                    onChange={(e) => setRelationshipType(e.target.value as 'sponsor' | 'sponsee' | 'friend')}
                    className={styles.select}
                    required
                  >
                    <option value="sponsor">My Sponsor</option>
                    <option value="sponsee">My Sponsee</option>
                    <option value="friend">Recovery Friend</option>
                  </select>
                </div>

                {(relationshipType === 'sponsor' || relationshipType === 'sponsee') && (
                  <div className={styles.formGroup}>
                    <label htmlFor="program" className={styles.label}>
                      Program <span className={styles.required}>*</span>
                    </label>
                    <select
                      id="program"
                      value={program}
                      onChange={(e) => setProgram(e.target.value)}
                      className={styles.select}
                      required
                    >
                      <option value="SAA">SAA - Sex Addicts Anonymous</option>
                      <option value="AA">AA - Alcoholics Anonymous</option>
                      <option value="NA">NA - Narcotics Anonymous</option>
                      <option value="CA">CA - Cocaine Anonymous</option>
                      <option value="MA">MA - Marijuana Anonymous</option>
                      <option value="GA">GA - Gamblers Anonymous</option>
                      <option value="OA">OA - Overeaters Anonymous</option>
                      <option value="ACA">ACA - Adult Children of Alcoholics</option>
                      <option value="CoDA">CoDA - Codependents Anonymous</option>
                      <option value="Al-Anon">Al-Anon</option>
                    </select>
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="message" className={styles.label}>
                  Personal Message (Optional)
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a personal message to your invitation..."
                  className={styles.textarea}
                  rows={3}
                />
              </div>

              {sendError && (
                <div className={styles.error}>{sendError}</div>
              )}

              <button
                type="submit"
                className={styles.sendButton}
                disabled={sending}
              >
                {sending ? 'Sending...' : 'Send Invitation'}
              </button>
            </form>
          </div>

          {/* SENT INVITATIONS HISTORY */}
          <div className={styles.historySection}>
            <h3 className={styles.historyTitle}>Sent Invitations</h3>
            
            {loadingHistory && (
              <div className={styles.loading}>Loading history...</div>
            )}

            {historyError && (
              <div className={styles.error}>{historyError}</div>
            )}

            {!loadingHistory && sentInvitations.length === 0 && (
              <div className={styles.emptyState}>
                No invitations sent yet. Send your first invitation above!
              </div>
            )}

            {!loadingHistory && sentInvitations.length > 0 && (
              <div className={styles.invitationsList}>
                {sentInvitations.map((invitation) => (
                  <div key={invitation.invitation_id} className={styles.invitationItem}>
                    <div className={styles.invitationInfo}>
                      <div className={styles.invitationHeader}>
                        <div className={styles.toEmail}>{invitation.to_email}</div>
                        <div 
                          className={styles.statusBadge} 
                          data-status={invitation.status}
                        >
                          {statusLabels[invitation.status] || invitation.status}
                        </div>
                      </div>
                      <div className={styles.invitationMeta}>
                        <span className={styles.relationship}>
                          {relationshipLabels[invitation.relationship_type]}
                        </span>
                        {invitation.program && (
                          <span className={styles.program}> • {invitation.program}</span>
                        )}
                        <span className={styles.date}> • {formatDate(invitation.created_at)}</span>
                      </div>
                      {invitation.message && (
                        <div className={styles.invitationMessage}>
                          "{invitation.message}"
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteManagement;

